import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    IconButton, 
    TextField,
    InputAdornment,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Business as BusinessIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import vendorService from '../../services/vendorService';
import VendorsTable from './VendorsTable';

const VendorsPage = () => {
    const navigate = useNavigate();
    const [vendors, setVendors] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [syncing, setSyncing] = useState(false);

    // Load vendors from IndexedDB
    const loadVendors = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await vendorService.getVendors();
            setVendors(data);
        } catch (err) {
            console.error('Error loading vendors:', err);
            setError('Failed to load vendors. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Sync vendors from server
    const syncVendors = async () => {
        try {
            setSyncing(true);
            setError(null);
            await vendorService.syncVendors();
            await loadVendors();
        } catch (err) {
            console.error('Error syncing vendors:', err);
            setError('Failed to sync vendors. Please check your connection.');
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        loadVendors();
    }, []);

    if (loading) {
        return (
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                height: '100vh' 
            }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ 
            position: 'fixed',
            top: '60px',
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: 'background.default'
        }}>
            {/* Header */}
            <Box 
                sx={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    flex: '0 0 auto'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            <BusinessIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Vendors
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            Total: {vendors.length} vendors
                        </Typography>
                    </Box>
                </Box>
                
                {/* Sync button hidden but functionality kept */}
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert 
                    severity="error" 
                    onClose={() => setError(null)}
                    sx={{ mx: 2, mt: 2, flex: '0 0 auto' }}
                >
                    {error}
                </Alert>
            )}

            {/* Search Bar */}
            <Box sx={{ px: 2, py: 2, flex: '0 0 auto' }}>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search by vendor name or number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'background.paper',
                        }
                    }}
                />
            </Box>

            {/* Vendors Table */}
            <Box sx={{ flex: '1 1 auto', px: 2, pb: 2, overflow: 'hidden' }}>
                {vendors.length === 0 ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        height: '100%',
                        gap: 2
                    }}>
                        <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
                        <Typography variant="h6" color="text.secondary">
                            No vendors found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Click the refresh button to sync vendors from the server
                        </Typography>
                    </Box>
                ) : (
                    <VendorsTable 
                        vendors={vendors} 
                        searchTerm={searchTerm}
                    />
                )}
            </Box>
        </Box>
    );
};

export default VendorsPage;